/*
  RetrieveData.jsx
  ----------------
  Interface pour récupérer les attestations d'un modèle.
*/

import { useState, useEffect, useMemo } from 'react';
import { Box, Text, Button, Stack, Select, Checkbox, Loader, Modal, Group, ActionIcon, Center, Menu, rem } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import 'mantine-react-table/styles.css';
import { DateInput } from '@mantine/dates';
import { CodeHighlight } from '@mantine/code-highlight';
import { useUser } from '../tools/Context';
import { IconApi, IconDotsVertical, IconFileInfo } from '@tabler/icons-react';
import { get, post } from '../tools/Request';
import { useNavigate, useLocation } from 'react-router-dom';

const REACT_APP_BLOCKCHAIN_EXPLORER_TX = process.env.REACT_APP_BLOCKCHAIN_EXPLORER_TX;

export default function RetrieveData() {
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedContractData, setSelectedContractData] = useState('');
  const [variables, setVariables] = useState([]);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [requestPreview, setRequestPreview] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rowDataModalOpened, setRowDataModalOpened] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const user = useUser();
  const { refreshPage } = useUser();
  const [selectedContractId, setSelectedContractId] = useState('');
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalAPI, setModalAPI] = useState(false);
  const location = useLocation();
  const [autoSelected, setAutoSelected] = useState(false);

  const navigate = useNavigate();

  const isNoDataError = (payload) => {
    const msg = payload?.error || payload?.message || '';
    const details = payload?.details || '';
    const asString = `${msg} ${details}`.toLowerCase();

    return asString.includes('aucune attestation') || asString.includes('no data found') || asString.includes('no data');
  };

  const extractApiError = (err) => {
    const data = err?.response?.data;

    if (data?.error) return data.error;
    if (data?.message) return data.message;
    if (typeof data === 'string') return data;
    if (err?.message) return err.message;

    return 'Erreur inconnue';
  };

  useEffect(() => {
    const fetchContracts = async () => {
      refreshPage(false);
      try {
        const response = await get('contract', {}, localStorage.getItem('userToken'));
        setContracts(response);
      } catch (error) {
        console.error('Error fetching contracts:', error);
        setContracts([]);
        setSelectedContract('');
        setVariables([]);
        setSelectedVariables([]);
      }
    };
    fetchContracts();
  }, [user, refreshPage]);

  const handleContractChange = (modelName) => {
    setSelectedContract(modelName);
    const selected = contracts.find((contract) => contract.name === modelName);
    setSelectedContractData(selected);
    setSelectedContractId(selected?.id || '');

    if (selected) {
      const contractVariables = selected?.variable?.variables || [];
      setVariables(contractVariables);
      setSelectedVariables(contractVariables.map((variable) => variable.name));
    } else {
      setVariables([]);
      setSelectedVariables([]);
    }
  };

  useEffect(() => {
    if (!contracts.length || autoSelected) return;

    const params = new URLSearchParams(location.search);
    const selectedName = params.get('select');
    if (!selectedName) return;

    const exists = contracts.find((c) => c.name === selectedName);
    if (exists) {
      handleContractChange(selectedName);
      setAutoSelected(true);
    }
  }, [contracts, location.search, autoSelected]);

  const handleCheckboxChange = (event, variableName) => {
    if (event.target.checked) {
      setSelectedVariables((prev) => [...prev, variableName]);
    } else {
      setSelectedVariables((prev) => prev.filter((name) => name !== variableName));
    }
  };

  useEffect(() => {
    try {
      if (!selectedContract || selectedVariables.length === 0) {
        setRequestPreview('Veuillez sélectionner un modèle, des variables et une plage de dates.');
        return;
      }

      const variablesString = selectedVariables.join(',');
      const queryParams = {
        modelId: selectedContractId,
        variables: variablesString,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null
      };

      const requestDetails = {
        url: 'http://localhost:3002/contractEvent',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        query: queryParams
      };
      setRequestPreview(JSON.stringify(requestDetails, null, 2));
    } catch {
      setRequestPreview('Invalid input or error while building the request preview.');
    }
  }, [selectedContract, selectedVariables, startDate, endDate, selectedContractId]);

  const handleSubmitFields = async () => {
    try {
      setLoading(true);
      setEventsData([]);
      setModalOpened(false);

      if (!selectedContract || selectedVariables.length === 0) {
        showNotification({
          title: 'Erreur',
          message: 'Veuillez sélectionner un modèle, au moins une variable et une plage de dates.',
          color: 'red',
          autoClose: 3000
        });
        return;
      }

      const variablesString = selectedVariables.join(',');

      const response = await get(
        'attestation',
        {
          contract_id: selectedContractId,
          variables: variablesString,
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null
        },
        localStorage.getItem('userToken')
      );

      if (response?.success === false) {
        if (isNoDataError(response)) {
          showNotification({
            title: 'Aucune donnée disponible',
            message: response?.error || "Aucune donnée n'a été trouvée pour les critères sélectionnés.",
            color: 'orange',
            autoClose: 3500
          });
          setEventsData([]);
          return;
        }

        showNotification({
          title: 'Une erreur est survenue',
          message: response?.error || 'Erreur serveur',
          color: 'red',
          autoClose: 3500
        });
        return;
      }

      const data = response?.data ?? [];

      if (!Array.isArray(data) || data.length === 0) {
        showNotification({
          title: 'Aucune donnée disponible',
          message: "Aucune donnée n'a été trouvée pour les critères sélectionnés.",
          color: 'orange',
          autoClose: 3000
        });
        setEventsData([]);
        return;
      }

      setEventsData(data);
      setModalOpened(true);
      showNotification({
        title: 'Succès',
        message: `Attestations récupérées avec succès.`,
        color: 'green',
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error fetching events:', error);

      if (error?.success === false) {
        showNotification({
          title: 'Aucune donnée disponible',
          message: error.messagee || "Aucune donnée n'a été trouvée pour les critères sélectionnés.",
          color: 'orange',
          autoClose: 3500
        });
        setEventsData([]);
        return;
      }

      showNotification({
        title: 'Une erreur est survenue',
        message: extractApiError(error),
        color: 'red',
        autoClose: 3500
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmRevoke = async () => {
    try {
      setConfirmLoading(true);
      await post('attestation/revoke', { dataId: selectedRowData.id }, localStorage.getItem('userToken'));
      showNotification({
        title: 'Succès',
        message: `Attestations révoquées avec succès.`,
        color: 'green',
        autoClose: 3000
      });
      handleSubmitFields();
      setConfirmModalOpened(false);
    } catch (error) {
      console.error('Error during revoke operation:', error);
      showNotification({
        title: 'Une erreur est survenue',
        message: `Erreur : ${error.message}`,
        color: 'red',
        autoClose: 3000
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRevokeClick = (rowData) => {
    setSelectedRowData(rowData);
    setConfirmModalOpened(true);
  };

  const columns = useMemo(() => {
    if (eventsData.length === 0) return [];

    const dateColumn = {
      accessorKey: 'timestamp',
      header: 'Date',
      Cell: ({ cell }) => {
        const timestamp = cell.getValue();
        return new Date(timestamp * 1000).toLocaleString();
      }
    };

    const displayProof = {
      header: 'Voir la preuve',
      Cell: ({ row }) => <Button onClick={() => navigate(`/proof?uid=${row.original.id}`)}>Voir la preuve</Button>
    };

    const otherColumns = Object.keys(eventsData[0])
      .filter((key) => key !== 'timestamp' && key !== 'contractName' && key !== 'revoked')
      .map((key) => ({
        accessorKey: key,
        header: key.charAt(0).toUpperCase() + key.slice(1),
        Cell: ({ cell }) =>
          key === 'transactionHash' ? (
            <a href={`${REACT_APP_BLOCKCHAIN_EXPLORER_TX}${cell.getValue()}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1E90FF', textDecoration: 'underline' }}>
              {cell.getValue()}
            </a>
          ) : typeof eventsData[0][key] === 'boolean' ? (
            <Text color={cell.getValue() ? 'green' : 'red'} weight="bold">
              {cell.getValue() ? 'True' : 'False'}
            </Text>
          ) : (
            <Text>{String(cell.getValue())}</Text>
          )
      }));

    const revokedColumn = {
      header: 'Révoquer',
      Cell: ({ row }) =>
        row.original.revoked ? <Text style={{ color: 'red', fontWeight: 'bold' }}>Révoqué</Text> : <Button onClick={() => handleRevokeClick(row.original)}>Révoquer</Button>
    };

    const isRevocable = selectedContractData?.variable?.revocable;
    return isRevocable ? [dateColumn, ...otherColumns, displayProof, revokedColumn] : [dateColumn, ...otherColumns, displayProof];
  }, [eventsData, navigate, selectedContractData?.variable?.revocable]);

  const table = useMantineReactTable({
    columns,
    data: eventsData
  });

  return (
    <>
      <Center>
        <Box
          mt="5%"
          backgroundColor="white"
          maw={800}
          style={{
            flex: 1,
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <Text style={{ fontSize: '1.25rem', fontWeight: 500 }}>Récupérer des attestations</Text>

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon size="lg" variant="light">
                  <IconDotsVertical style={{ width: '60%', height: '60%' }} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  component="a"
                  href={'/docs/docs/steps/detch-data'}
                  size="lg"
                  target="_blank"
                  variant="transparent"
                  leftSection={<IconFileInfo style={{ width: rem(14), height: rem(14) }} />}>
                  Voir la documentation
                </Menu.Item>
                <Menu.Item onClick={() => setModalAPI(true)} leftSection={<IconApi style={{ width: rem(14), height: rem(14) }} />}>
                  Aperçu de la requête
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          <Stack spacing="sm">
            <Select
              mt={16}
              placeholder="Choisir un modèle"
              error={contracts.length === 0 && 'Veuillez créer un modèle'}
              value={selectedContract}
              onChange={handleContractChange}
              data={contracts.map((contract) => contract.name)}
            />

            <div
              style={{
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '0.5rem'
              }}>
              <Stack h={254}>
                {variables.map((variable) => (
                  <Checkbox
                    key={variable.name}
                    label={variable.name}
                    value={variable.name}
                    checked={selectedVariables.includes(variable.name)}
                    onChange={(e) => handleCheckboxChange(e, variable.name)}
                  />
                ))}
              </Stack>
            </div>

            <Group justify="center" style={{ marginTop: 10 }}>
              <Text>Du</Text>
              <DateInput ml={-7} mr={-7} value={startDate} onChange={setStartDate} placeholder="" valueFormat="DD/MM/YYYY" clearable />
              <Text>au</Text>
              <DateInput ml={-7} value={endDate} onChange={setEndDate} placeholder="" valueFormat="DD/MM/YYYY" clearable />
            </Group>

            <Group justify="center">
              <Button w={212} color="#0a0082" onClick={handleSubmitFields} disabled={loading}>
                {loading ? <Loader size="xs" color="white" /> : 'Récupérer les attestations'}
              </Button>
            </Group>

            <Center>
              <Button w="30%" onClick={() => setModalOpened(true)} disabled={eventsData.length === 0}>
                Voir les attestations
              </Button>
            </Center>
          </Stack>

          <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Attestatation récupérées" size="100%">
            {eventsData.length === 0 ? <Text>Aucune attestations disponible</Text> : <MantineReactTable table={table} />}
          </Modal>

          <Modal opened={rowDataModalOpened} onClose={() => setRowDataModalOpened(false)} title="Attestations de la ligne" size="lg">
            <Stack>
              {selectedRowData &&
                Object.entries(selectedRowData).map(([key, value]) => (
                  <Text key={key}>
                    <strong>{key}:</strong> {String(value)}
                  </Text>
                ))}
            </Stack>
          </Modal>

          <Modal opened={confirmModalOpened} onClose={() => setConfirmModalOpened(false)} title="Confirmation de révocation" size="md">
            <Text>Êtes-vous sûr de vouloir révoquer cette donnée ?</Text>
            <Group position="apart" mt="md">
              <Button variant="outline" onClick={() => setConfirmModalOpened(false)}>
                Annuler
              </Button>
              <Button color="red" onClick={confirmRevoke} disabled={confirmLoading} leftIcon={confirmLoading && <Loader size="xs" color="white" />}>
                {confirmLoading ? 'Révocation...' : 'Confirmer'}
              </Button>
            </Group>
          </Modal>
        </Box>
      </Center>

      <Modal opened={modalAPI} onClose={() => setModalAPI(false)} title="Aperçu de la requête" size="lg">
        <CodeHighlight code={requestPreview} language="json" copyLabel="Copier" copiedLabel="Copié!" />
      </Modal>
    </>
  );
}
